<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter, onBeforeRouteLeave, type RouteLocationNormalized } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/useAuthStore'
import useCalaUser from '@/composables/UseCalaUser'
import { useApplyDarkMode } from '@/utils/theme'

import CustomButton from '@/components/customButton/CustomButton.vue'

const authStore = useAuthStore()
const { getCalaUser, updateCalaUser, wontRegisterCalaUser, loading } = useCalaUser()
const { t } = useI18n()
const router = useRouter()
const userInfo = ref({
  ssn: '',
  name: '',
  email: '',
  phoneNumber: '',
  updateExisting: false,
  acceptPromotionalEmail: false,
  preferDarkMode: false,
})

const originalUserInfo = ref<Record<string, unknown>>({})
const formChanged = ref(false)
const phoneError = ref('')
const userInfoError = ref(false)
const userInfoErrorMessage = ref('')
const userInfoSuccess = ref(false)
const userInfoSuccessMessage = ref('')
const showModal = ref(false)
const modalConfirmed = ref(false)
const desiredRoute = ref<RouteLocationNormalized | null>(null)

const setDarkMode = useApplyDarkMode()

function cleanPhoneNumber(raw: string): string {
  return raw.replace(/[\s\-\+\(\)]/g, '')
}

function validatePhone(): boolean {
  const raw = userInfo.value.phoneNumber
  if (!raw) {
    phoneError.value = ''
    return true
  }
  const cleaned = cleanPhoneNumber(raw)
  if (!/^\d+$/.test(cleaned)) {
    phoneError.value = t('umsokn_umsaekjandi_simanumer_invalid')
    return false
  }
  if (cleaned.length < 7 || cleaned.length > 15) {
    phoneError.value = t('umsokn_umsaekjandi_simanumer_invalid')
    return false
  }
  phoneError.value = ''
  return true
}

onMounted(fetchUserInfo)

async function fetchUserInfo() {
  const user = authStore.authUser
  userInfo.value.name = user?.name || ''
  userInfo.value.ssn = user?.username || ''

  try {
    const { user: customer } = await getCalaUser()

    const c =
      customer ??
      (Array.isArray(customer) ? customer[0] : null) ??
      (customer?.item1 ?? null)

    userInfo.value.phoneNumber = c?.phoneNumber || ''
    userInfo.value.email = c?.email || ''
    userInfo.value.acceptPromotionalEmail = c?.acceptPromotionalEmail ?? false
    userInfo.value.preferDarkMode = c?.preferDarkMode ?? false
    userInfo.value.updateExisting = !!c

    setDarkMode(!!userInfo.value.preferDarkMode)
} catch (e: any) {
  console.error(e)
  const status = e?.response?.status ?? e?.status
  if (status === 404) {
    // No existing profile -> treat as "not found" without showing an error
    userInfo.value.updateExisting = false
    // leave other fields as empty defaults
  } else {
    userInfoError.value = true
    userInfoErrorMessage.value = t('unable_to_fetch_user_info')
  }
}

  originalUserInfo.value = JSON.parse(JSON.stringify(userInfo.value))
}

async function submitUserInfoHandler() {
  userInfoError.value = false
  userInfoErrorMessage.value = ''
  userInfoSuccess.value = false
  userInfoSuccessMessage.value = ''

  if (!validatePhone()) return

  userInfo.value.phoneNumber = cleanPhoneNumber(userInfo.value.phoneNumber)

  try {
    await updateCalaUser(userInfo.value)
    userInfoSuccess.value = true
    userInfoSuccessMessage.value = t('changes_saved_successfully')
    originalUserInfo.value = JSON.parse(JSON.stringify(userInfo.value))
    formChanged.value = false

    setDarkMode(!!userInfo.value.preferDarkMode)
  } catch (e) {
    console.error(e)
    userInfoError.value = true
    userInfoErrorMessage.value = t('unable_to_save')
  }
}

watch(
  userInfo,
  () => {
    formChanged.value =
      JSON.stringify(userInfo.value) !== JSON.stringify(originalUserInfo.value)
  },
  { deep: true }
)

onBeforeRouteLeave((to, from, next) => {
  if (!userInfo.value.updateExisting && formChanged.value && !modalConfirmed.value) {
    desiredRoute.value = to
    showModal.value = true
    next(false)
  } else {
    next()
  }
})

async function confirmLeave() {
  showModal.value = false
  modalConfirmed.value = true
  try {
    await wontRegisterCalaUser(userInfo.value.ssn)
  } catch (e) {
    console.error(e)
  }
  if (desiredRoute.value) router.push(desiredRoute.value)
}

function cancelLeave() {
  showModal.value = false
}
</script>


<template>
  <div>
    <h1 class="m-b-40">
      {{ t(userInfo.email ? 'stillingar_header' : 'stillingar_no_email_heading') }}
    </h1>
    <p>
      {{ t(userInfo.email ? 'stillingar_subHeader' : 'stillingar_no_email') }}
    </p>
    <form class="forms-extra" @submit.prevent="submitUserInfoHandler">
      <div class="center__container">
        <v-row>
          <v-col cols="12" md="12">
            <v-alert v-if="userInfoError" type="error" closable class="mt-4">
              <div>{{ userInfoErrorMessage }}</div>
            </v-alert>
            <v-alert v-if="userInfoSuccess" type="success" closable class="mt-4">
              <div>{{ userInfoSuccessMessage }}</div>
            </v-alert>
          </v-col>
          <v-col cols="12" md="6" class="settings-form">
            <v-text-field
              id="name"
              v-model="userInfo.name"
              :label="t('stillingar_Nafn')"
              :placeholder="t('stillingar_Nafn')"
              disabled
            />

            <v-text-field
              id="ssn"
              v-model="userInfo.ssn"
              :label="t('stillingar_Kennitala')"
              :placeholder="t('stillingar_Kennitala')"
              disabled
            />

            <v-text-field
              id="email"
              v-model="userInfo.email"
              :label="t('stillingar_Netfang')"
              :placeholder="t('stillingar_Netfang')"
              type="email"
            />

            <v-text-field
              id="phone"
              v-model="userInfo.phoneNumber"
              :label="t('stillingar_Simi')"
              :placeholder="t('stillingar_Simi')"
              :error-messages="phoneError"
              @blur="validatePhone"
            />

            <div class="prefs">
              <v-checkbox
                v-model="userInfo.acceptPromotionalEmail"
                :label="t('stillingar_TolvuposturSamthykkt')"
                hide-details
              />
              <v-checkbox
                v-model="userInfo.preferDarkMode"
                :label="t('stillingar_PreferDarkMode')"
                hide-details
              />
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <CustomButton :disabled="!formChanged" :loading="loading" type="submit">
              Vista
            </CustomButton>
          </v-col>
        </v-row>
      </div>
    </form>

    <v-dialog v-model="showModal" persistent width="400">
      <v-card>
        <v-card-title class="text-h5">
          {{ t('stillingar_unsaved_changes') }}
        </v-card-title>
        <v-card-text>
          {{ t('stillingar_unsaved_changes_message') }}
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="confirmLeave">
            {{ t('stillingar_leave') }}
          </v-btn>
          <v-btn variant="text" @click="cancelLeave">
            {{ t('stillingar_stay') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.forms-extra { margin: 0; }
.settings-form { display: flex; flex-direction: column; gap: 1rem; }
.prefs { display: grid; gap: 0.5rem; padding-top: 0.5rem; }
</style>
